import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Calendar, Clock, Mail, User, MapPin, 
  Link as LinkIcon, Phone, FileText, Plus, Edit2, Eye 
} from "lucide-react";

export default function AppointmentScheduling() {
  const navigate = useNavigate();
  const [view, setView] = useState("list"); // 'list' or 'form'
  const [editingId, setEditingId] = useState(null);
  
  const [form, setForm] = useState({
    appointmentType: "",
    otherAppointmentType: "",
    visitorName: "",
    email: "",
    mobile: "",
    purpose: "",
    appointmentDate: "",
    appointmentTime: "",
    duration: "",
    mode: "",
    meetingLink: "",
    venue: "",
    assignedTo: "",
    assignedMobile: "",
    notes: "",
  });

  const [appointments, setAppointments] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("scheduledAppointments") || "[]");
    } catch {
      return [];
    }
  });

  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSchedule = () => {
    if (!form.email || !form.appointmentDate || !form.appointmentTime) {
      alert("Email, date, and time are required");
      return;
    }

    const newAppointment = {
      id: editingId || Date.now(),
      ...form,
      scheduledOn: new Date().toLocaleString(),
    };

    const updated = editingId
      ? appointments.map((a) => (a.id === editingId ? newAppointment : a))
      : [newAppointment, ...appointments];
    
    setAppointments(updated);
    localStorage.setItem("scheduledAppointments", JSON.stringify(updated));

    alert(`Appointment ${editingId ? "rescheduled" : "scheduled"} successfully.`);
    resetForm();
    setView("list");
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      appointmentType: "", otherAppointmentType: "", visitorName: "",
      email: "", mobile: "", purpose: "", appointmentDate: "",
      appointmentTime: "", duration: "", mode: "", meetingLink: "",
      venue: "", assignedTo: "", assignedMobile: "", notes: "",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-8" style={robotoStyle}>
      <div className="max-w-6xl mx-auto">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <button 
              onClick={() => navigate("/admin/letter-template-mgmt")}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-2 transition-colors text-sm font-medium"
            >
              <ArrowLeft size={16} /> Back to Management
            </button>
            <h1 className="text-2xl font-semibold text-slate-900" style={poppinsStyle}>
              Appointment Letter & Scheduling
            </h1>
            <p className="text-sm text-slate-500">Manage interviews, meetings, and official appointments.</p>
          </div>
          
          {view === "list" && (
            <button
              onClick={() => { resetForm(); setView("form"); }}
              className="flex items-center gap-2 bg-slate-900 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm"
            >
              <Plus size={16} /> Schedule New
            </button>
          )}
        </div>

        {view === "form" ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm animate-in slide-in-from-bottom-4 duration-500">
            <h2 className="text-lg font-semibold mb-6 text-slate-800 border-b pb-4">Appointment Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Select
                label="Appointment Type"
                value={form.appointmentType}
                onChange={(v) => handleChange("appointmentType", v)}
                options={["Interview", "Demo", "Client Meeting", "Vendor Meeting", "Other"]}
              />

              {form.appointmentType === "Other" && (
                <Input
                  label="Specific Type"
                  value={form.otherAppointmentType}
                  onChange={(v) => handleChange("otherAppointmentType", v)}
                />
              )}

              <Input label="Visitor Name" icon={<User size={14}/>} value={form.visitorName} onChange={(v) => handleChange("visitorName", v)} />
              <Input label="Email ID" icon={<Mail size={14}/>} value={form.email} onChange={(v) => handleChange("email", v)} />
              <Input label="Mobile Number" icon={<Phone size={14}/>} value={form.mobile} onChange={(v) => handleChange("mobile", v)} />
              <Input label="Purpose" value={form.purpose} onChange={(v) => handleChange("purpose", v)} />
              <Input label="Date" type="date" icon={<Calendar size={14}/>} value={form.appointmentDate} onChange={(v) => handleChange("appointmentDate", v)} />
              <Input label="Time" type="time" icon={<Clock size={14}/>} value={form.appointmentTime} onChange={(v) => handleChange("appointmentTime", v)} />
              
              <Select
                label="Duration"
                value={form.duration}
                onChange={(v) => handleChange("duration", v)}
                options={["15 Mins", "30 Mins", "45 Mins", "1 Hour", "2 Hours"]}
              />

              <Select
                label="Mode"
                value={form.mode}
                onChange={(v) => handleChange("mode", v)}
                options={["Online", "Offline"]}
              />

              {form.mode === "Online" && (
                <Input label="Meeting Link" icon={<LinkIcon size={14}/>} value={form.meetingLink} onChange={(v) => handleChange("meetingLink", v)} />
              )}

              {form.mode === "Offline" && (
                <Input label="Venue" icon={<MapPin size={14}/>} value={form.venue} onChange={(v) => handleChange("venue", v)} />
              )}

              <Input label="Assigned By" value={form.assignedTo} onChange={(v) => handleChange("assignedTo", v)} />
            </div>

            <div className="mt-6">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Additional Notes</label>
              <textarea
                className="w-full border border-slate-200 rounded-xl px-4 py-3 h-24 bg-slate-50/30 focus:ring-1 focus:ring-indigo-500 outline-none transition-all text-sm"
                value={form.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
              />
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setView("list")} className="px-6 py-2 text-sm font-medium text-slate-500 hover:text-slate-900">Cancel</button>
              <button
                onClick={handleSchedule}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-2 rounded-lg text-sm font-medium transition-all shadow-md"
              >
                {editingId ? "Update Appointment" : "Confirm Schedule"}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-slate-100 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Candidate</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Schedule</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Type/Mode</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {appointments.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-20 text-center opacity-40">
                      <FileText size={48} className="mx-auto mb-2" />
                      <p>No appointments found</p>
                    </td>
                  </tr>
                ) : (
                  appointments.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">{a.visitorName}</div>
                        <div className="text-xs text-slate-400">{a.email}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        <div>{a.appointmentDate}</div>
                        <div className="text-xs text-slate-400">{a.appointmentTime} ({a.duration})</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide ${a.mode === 'Online' ? 'bg-blue-50 text-blue-600' : 'bg-orange-50 text-orange-600'}`}>
                          {a.appointmentType} • {a.mode}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button onClick={() => { setForm(a); setEditingId(a.id); setView("form"); }} className="p-2 text-slate-400 hover:text-indigo-600 transition-colors"><Edit2 size={16} /></button>
                          <button onClick={() => alert(`Details: ${a.purpose}\nNotes: ${a.notes}`)} className="p-2 text-slate-400 hover:text-slate-900 transition-colors"><Eye size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Reusable Components
function Input({ label, value, onChange, type = "text", icon, placeholder }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</label>
      <div className="relative flex items-center">
        {icon && <div className="absolute left-3 text-slate-400">{icon}</div>}
        <input
          type={type} value={value} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full border border-slate-200 rounded-lg ${icon ? 'pl-9' : 'px-4'} py-2 bg-slate-50/30 focus:ring-1 focus:ring-indigo-500 focus:bg-white outline-none transition-all text-sm`}
        />
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</label>
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-200 rounded-lg px-4 py-2 bg-slate-50/30 focus:ring-1 focus:ring-indigo-500 focus:bg-white outline-none transition-all text-sm"
      >
        <option value="">Select</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}